import React from 'react';

export interface UseMapActions<T extends object> {
  get: (key: keyof T) => T[keyof T];
  set: <K extends keyof T>(key: K, value: T[K]) => void;
  remove: <K extends keyof T>(key: K) => void;
  reset: () => void;
}

export const useMap = <T extends object = any>(initialMap: T = {} as T): [T, UseMapActions<T>] => {
  const initialMapRef = React.useRef(initialMap);

  const [map, setMap] = React.useState<T>(initialMap);

  const get = React.useCallback(
    (key: keyof T) => {
      return map[key];
    },
    [map],
  );

  const set = React.useCallback((key: keyof T, value: T[keyof T]) => {
    setMap((prevMap) => ({
      ...prevMap,
      [key]: value,
    }));
  }, []);

  const remove = React.useCallback((key: keyof T) => {
    setMap((prevMap) => {
      const { [key]: _omit, ...rest } = prevMap;
      return rest as T;
    });
  }, []);

  const reset = React.useCallback(() => {
    setMap(initialMapRef.current);
  }, []);

  const actions: UseMapActions<T> = React.useMemo(
    () => ({
      get,
      set,
      remove,
      reset,
    }),
    [get, set, remove, reset],
  );

  return [map, actions];
};
